'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface Booking {
  id: string;
  ref: string;
  client: string;
  destination: string;
  type: string;
  date: string;
  value: number;
  commission: number;
  status: 'confirmed' | 'pending' | 'cancelled';
}

interface PartnerStats {
  totalBookings: number;
  totalRevenue: number;
  totalCommission: number;
  pendingCommission: number;
  conversionRate: number;
  avgBookingValue: number;
}

const mockBookings: Booking[] = [
  { id: '1', ref: 'TG-2024-001', client: 'Ahmed Hassan', destination: 'Dubai', type: 'Flight + Hotel', date: '2024-03-15', value: 1850, commission: 185, status: 'confirmed' },
  { id: '2', ref: 'TG-2024-002', client: 'Sarah Johnson', destination: 'Maldives', type: 'Holiday Package', date: '2024-03-18', value: 3200, commission: 320, status: 'confirmed' },
  { id: '3', ref: 'TG-2024-003', client: 'Mohammed Ali', destination: 'Istanbul', type: 'Flight', date: '2024-03-20', value: 680, commission: 68, status: 'pending' },
  { id: '4', ref: 'TG-2024-004', client: 'Fatima Malik', destination: 'Umrah Package', type: 'Holiday', date: '2024-03-22', value: 2100, commission: 210, status: 'confirmed' },
  { id: '5', ref: 'TG-2024-005', client: 'David Chen', destination: 'Bangkok', type: 'Flight + Hotel', date: '2024-03-25', value: 1450, commission: 145, status: 'pending' },
  { id: '6', ref: 'TG-2024-006', client: 'Aisha Patel', destination: 'Cancun', type: 'Holiday Package', date: '2024-03-28', value: 2800, commission: 280, status: 'confirmed' },
];

const stats: PartnerStats = {
  totalBookings: 47,
  totalRevenue: 68500,
  totalCommission: 6850,
  pendingCommission: 1208,
  conversionRate: 34.2,
  avgBookingValue: 1457,
};

const performanceData = [
  { month: 'Oct', bookings: 6, commission: 820 },
  { month: 'Nov', bookings: 8, commission: 1100 },
  { month: 'Dec', bookings: 12, commission: 1680 },
  { month: 'Jan', bookings: 7, commission: 950 },
  { month: 'Feb', bookings: 9, commission: 1250 },
  { month: 'Mar', bookings: 5, commission: 1050 },
];

const maxCommission = Math.max(...performanceData.map(d => d.commission));

export default function PartnerAgencyDashboard() {
  const [activeTab, setActiveTab] = useState<'overview' | 'bookings' | 'commissions' | 'resources'>('overview');
  const [statusFilter, setStatusFilter] = useState<'all' | 'confirmed' | 'pending' | 'cancelled'>('all');

  const filteredBookings = statusFilter === 'all'
    ? mockBookings
    : mockBookings.filter(b => b.status === statusFilter);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
              <Icon name="BuildingOffice2Icon" size={22} className="text-white" />
            </div>
            <div>
              <h1 className="font-heading font-black text-2xl text-foreground">Partner Agency Dashboard</h1>
              <p className="text-sm text-muted-foreground">Welcome back, Travel Partner</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="bg-green-100 text-green-700 font-bold text-xs px-3 py-1.5 rounded-full flex items-center gap-1">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            Active Partner
          </span>
          <Link
            href="/partner-integration"
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-secondary text-white font-bold rounded-xl hover:shadow-lg transition-all text-sm"
          >
            <Icon name="PlusIcon" size={16} />
            New Booking
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { label: 'Total Bookings', value: stats.totalBookings.toString(), icon: 'TicketIcon', color: 'text-blue-600', bg: 'bg-blue-50', change: '+12%' },
          { label: 'Total Revenue', value: `£${stats.totalRevenue.toLocaleString()}`, icon: 'CurrencyPoundIcon', color: 'text-green-600', bg: 'bg-green-50', change: '+18%' },
          { label: 'Total Commission', value: `£${stats.totalCommission.toLocaleString()}`, icon: 'BanknotesIcon', color: 'text-purple-600', bg: 'bg-purple-50', change: '+15%' },
          { label: 'Pending Commission', value: `£${stats.pendingCommission.toLocaleString()}`, icon: 'ClockIcon', color: 'text-orange-600', bg: 'bg-orange-50', change: '' },
          { label: 'Conversion Rate', value: `${stats.conversionRate}%`, icon: 'ArrowTrendingUpIcon', color: 'text-teal-600', bg: 'bg-teal-50', change: '+3.2%' },
          { label: 'Avg Booking Value', value: `£${stats.avgBookingValue.toLocaleString()}`, icon: 'ChartBarIcon', color: 'text-indigo-600', bg: 'bg-indigo-50', change: '+8%' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-2xl shadow-sm border border-border p-5">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 ${stat.bg} rounded-xl flex items-center justify-center`}>
                <Icon name={stat.icon as any} size={20} className={stat.color} />
              </div>
              {stat.change && (
                <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded-full">{stat.change}</span>
              )}
            </div>
            <p className="font-black text-2xl text-foreground">{stat.value}</p>
            <p className="text-xs text-muted-foreground font-bold mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-border p-1.5 flex gap-1 flex-wrap">
        {(['overview', 'bookings', 'commissions', 'resources'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 min-w-[100px] px-4 py-2.5 rounded-lg font-bold text-sm capitalize transition-all ${
              activeTab === tab ? 'bg-primary text-white shadow-md' : 'text-foreground hover:bg-surface'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Performance Chart */}
          <div className="bg-white rounded-2xl shadow-sm border border-border p-6">
            <h3 className="font-heading font-black text-lg text-foreground mb-4">Monthly Commission</h3>
            <div className="flex items-end gap-2 h-40">
              {performanceData.map((d) => (
                <div key={d.month} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs font-bold text-primary">£{d.commission}</span>
                  <div
                    className="w-full bg-gradient-to-t from-primary to-secondary rounded-t-lg transition-all"
                    style={{ height: `${(d.commission / maxCommission) * 100}%` }}
                  />
                  <span className="text-xs text-muted-foreground font-bold">{d.month}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl shadow-sm border border-border p-6">
            <h3 className="font-heading font-black text-lg text-foreground mb-4">Quick Actions</h3>
            <div className="space-y-3">
              {[
                { label: 'Search Flights for Client', icon: 'MagnifyingGlassIcon', href: '/flight-search', color: 'bg-blue-50 text-blue-600' },
                { label: 'Create Holiday Package', icon: 'GlobeAltIcon', href: '/holidays', color: 'bg-green-50 text-green-600' },
                { label: 'View Commission Report', icon: 'CurrencyPoundIcon', href: '/commission-dashboard', color: 'bg-purple-50 text-purple-600' },
                { label: 'Track Client PNR', icon: 'MapPinIcon', href: '/live-pnr-dashboard', color: 'bg-orange-50 text-orange-600' },
                { label: 'Visa Requirements', icon: 'DocumentCheckIcon', href: '/visa-requirements-matrix', color: 'bg-teal-50 text-teal-600' },
              ].map((action) => (
                <Link
                  key={action.label}
                  href={action.href}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-surface transition-colors group"
                >
                  <div className={`w-9 h-9 ${action.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <Icon name={action.icon as any} size={18} />
                  </div>
                  <span className="font-bold text-sm text-foreground group-hover:text-primary transition-colors">{action.label}</span>
                  <Icon name="ChevronRightIcon" size={16} className="ml-auto text-muted-foreground" />
                </Link>
              ))}
            </div>
          </div>

          {/* Tide Payment */}
          <div className="bg-gradient-to-br from-[#00B4D8]/10 to-[#0096B4]/10 rounded-2xl border-2 border-[#00B4D8]/30 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#00B4D8] rounded-xl flex items-center justify-center">
                <span className="text-white font-black text-lg">T</span>
              </div>
              <div>
                <h3 className="font-heading font-black text-lg text-foreground">Tide Payment Portal</h3>
                <p className="text-xs text-muted-foreground">Accept client payments instantly</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="bg-white p-3 rounded-xl shadow-sm border border-border">
                <img
                  src="/assets/images/WhatsApp_Image_2026-03-01_at_7.24.40_AM-1772332114229.jpeg"
                  alt="Tide payment QR code for partner payments"
                  className="w-32 h-32 object-contain"
                />
              </div>
              <div className="flex-1 space-y-3">
                <p className="text-sm text-foreground">Share this QR code or payment link with your clients to collect payments directly.</p>
                <a
                  href="https://pay.tide.co/pay/93db3839-9be1-4d9c-adec-feac965f8aac"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full bg-[#00B4D8] text-white font-bold py-3 rounded-xl hover:bg-[#0096B4] transition-colors"
                >
                  <Icon name="ArrowTopRightOnSquareIcon" size={18} />
                  Open Payment Link
                </a>
                <button
                  onClick={() => navigator.clipboard.writeText('https://pay.tide.co/pay/93db3839-9be1-4d9c-adec-feac965f8aac')}
                  className="flex items-center justify-center gap-2 w-full border-2 border-[#00B4D8] text-[#00B4D8] font-bold py-2.5 rounded-xl hover:bg-[#00B4D8]/10 transition-colors text-sm"
                >
                  <Icon name="ClipboardDocumentIcon" size={16} />
                  Copy Payment Link
                </button>
              </div>
            </div>
          </div>

          {/* Top Destinations */}
          <div className="bg-white rounded-2xl shadow-sm border border-border p-6">
            <h3 className="font-heading font-black text-lg text-foreground mb-4">Top Destinations</h3>
            <div className="space-y-3">
              {[
                { dest: 'Dubai', bookings: 12, revenue: 18600 },
                { dest: 'Maldives', bookings: 8, revenue: 25600 },
                { dest: 'Istanbul', bookings: 7, revenue: 4760 },
                { dest: 'Umrah Package', bookings: 6, revenue: 12600 },
                { dest: 'Bangkok', bookings: 5, revenue: 7250 },
              ].map((dest, i) => (
                <div key={dest.dest} className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-primary/10 text-primary font-black text-xs rounded-full flex items-center justify-center">{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="font-bold text-sm text-foreground">{dest.dest}</span>
                      <span className="text-xs text-muted-foreground">{dest.bookings} bookings</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full">
                      <div
                        className="h-full bg-gradient-to-r from-primary to-secondary rounded-full"
                        style={{ width: `${(dest.bookings / 12) * 100}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs font-bold text-green-600">£{dest.revenue.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bookings Tab */}
      {activeTab === 'bookings' && (
        <div className="bg-white rounded-2xl shadow-sm border border-border overflow-hidden">
          <div className="p-5 border-b border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <h3 className="font-heading font-black text-lg text-foreground">Recent Bookings</h3>
            <div className="flex gap-2 flex-wrap">
              {(['all', 'confirmed', 'pending', 'cancelled'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize transition-all ${
                    statusFilter === s ? 'bg-primary text-white' : 'bg-surface text-foreground hover:bg-primary/10'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface">
                <tr>
                  {['Ref', 'Client', 'Destination', 'Type', 'Date', 'Value', 'Commission', 'Status'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-black text-muted-foreground uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-surface/50 transition-colors">
                    <td className="px-4 py-3 text-xs font-black text-primary">{booking.ref}</td>
                    <td className="px-4 py-3 text-sm font-bold text-foreground">{booking.client}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{booking.destination}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{booking.type}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{booking.date}</td>
                    <td className="px-4 py-3 text-sm font-bold text-foreground">£{booking.value.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm font-bold text-green-600">£{booking.commission}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold capitalize ${getStatusColor(booking.status)}`}>
                        {booking.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Commissions Tab */}
      {activeTab === 'commissions' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: 'Paid This Month', value: '£5,642', icon: 'CheckCircleIcon', color: 'text-green-600', bg: 'bg-green-50' },
              { label: 'Pending Payout', value: `£${stats.pendingCommission}`, icon: 'ClockIcon', color: 'text-orange-600', bg: 'bg-orange-50' },
              { label: 'Commission Rate', value: '10%', icon: 'PercentBadgeIcon', color: 'text-purple-600', bg: 'bg-purple-50' },
            ].map((item) => (
              <div key={item.label} className="bg-white rounded-2xl shadow-sm border border-border p-5 flex items-center gap-4">
                <div className={`w-12 h-12 ${item.bg} rounded-xl flex items-center justify-center`}>
                  <Icon name={item.icon as any} size={24} className={item.color} />
                </div>
                <div>
                  <p className="font-black text-2xl text-foreground">{item.value}</p>
                  <p className="text-xs text-muted-foreground font-bold">{item.label}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-border p-6">
            <h3 className="font-heading font-black text-lg text-foreground mb-4">Commission Structure</h3>
            <div className="space-y-3">
              {[
                { type: 'Flights', rate: '8%', min: '£5', max: '£150' },
                { type: 'Hotels', rate: '10%', min: '£10', max: '£200' },
                { type: 'Holiday Packages', rate: '12%', min: '£50', max: '£500' },
                { type: 'Onward Tickets', rate: '15%', min: '£3', max: '£30' },
                { type: 'Visa Services', rate: '10%', min: '£5', max: '£50' },
              ].map((item) => (
                <div key={item.type} className="flex items-center justify-between p-3 bg-surface rounded-xl">
                  <span className="font-bold text-sm text-foreground">{item.type}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-muted-foreground">Min: {item.min}</span>
                    <span className="text-xs text-muted-foreground">Max: {item.max}</span>
                    <span className="font-black text-primary bg-primary/10 px-3 py-1 rounded-full text-sm">{item.rate}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Resources Tab */}
      {activeTab === 'resources' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { title: 'Partner Integration Guide', desc: 'API docs, white-label setup, and integration tools', icon: 'CodeBracketIcon', href: '/partner-integration', color: 'bg-blue-50 text-blue-600' },
            { title: 'Commission Dashboard', desc: 'Detailed commission reports and payout history', icon: 'CurrencyPoundIcon', href: '/commission-dashboard', color: 'bg-green-50 text-green-600' },
            { title: 'Visa Requirements Matrix', desc: 'Check visa requirements for all destinations', icon: 'TableCellsIcon', href: '/visa-requirements-matrix', color: 'bg-purple-50 text-purple-600' },
            { title: 'Analytics Dashboard', desc: 'Track performance metrics and booking trends', icon: 'ChartBarIcon', href: '/analytics', color: 'bg-orange-50 text-orange-600' },
            { title: 'Contact Support', desc: 'Get help from our dedicated partner support team', icon: 'ChatBubbleLeftRightIcon', href: '/contact-support', color: 'bg-teal-50 text-teal-600' },
            { title: 'Trust Center', desc: 'Security certifications and compliance documents', icon: 'ShieldCheckIcon', href: '/trust-center', color: 'bg-indigo-50 text-indigo-600' },
          ].map((resource) => (
            <Link
              key={resource.title}
              href={resource.href}
              className="bg-white rounded-2xl shadow-sm border border-border p-5 hover:shadow-md transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 ${resource.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <Icon name={resource.icon as any} size={22} />
                </div>
                <div className="flex-1">
                  <h4 className="font-heading font-black text-base text-foreground group-hover:text-primary transition-colors">{resource.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{resource.desc}</p>
                </div>
                <Icon name="ChevronRightIcon" size={18} className="text-muted-foreground group-hover:text-primary transition-colors mt-1" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
